import type SpeedTest from '@cloudflare/speedtest'
import type { MeasurementConfig, Results } from '@cloudflare/speedtest'
import { useEffect, useRef, useState } from 'react'

export type SpeedTestPhase = 'idle' | 'running' | 'done' | 'error'

export interface SpeedTestState {
	phase: SpeedTestPhase
	direction?: 'download' | 'upload'
	downloadBps?: number
	uploadBps?: number
	loadedLatencyMs?: number
	usedBytes?: number
	/** Epoch ms of the last completed run. */
	finishedAt?: number
}

/** A run moves hundreds of MB, so opening the popover reuses a result this recent instead of testing again. */
const RESULT_REUSE_MS = 30 * 60_000

export const hasRecentResult = (state: SpeedTestState, now: number): boolean =>
	state.phase === 'done' && state.finishedAt !== undefined && now - state.finishedAt < RESULT_REUSE_MS

// The library's default sequence minus packet loss, which needs TURN credentials we do not have.
const MEASUREMENTS: MeasurementConfig[] = [
	{ type: 'latency', numPackets: 2 },
	{ type: 'download', bytes: 1e5, count: 1, bypassMinDuration: true },
	{ type: 'latency', numPackets: 20 },
	{ type: 'download', bytes: 1e5, count: 9 },
	{ type: 'download', bytes: 1e6, count: 8 },
	{ type: 'upload', bytes: 1e5, count: 8 },
	{ type: 'upload', bytes: 1e6, count: 6 },
	{ type: 'download', bytes: 1e7, count: 6 },
	{ type: 'upload', bytes: 1e7, count: 4 },
	{ type: 'download', bytes: 25e6, count: 4 },
	{ type: 'upload', bytes: 25e6, count: 4 },
	{ type: 'download', bytes: 1e8, count: 3 },
	{ type: 'upload', bytes: 5e7, count: 3 },
	{ type: 'download', bytes: 25e7, count: 2 },
]

const readResults = (results: Results) => {
	// The first loaded samples can read 0 before enough have arrived; no real round trip is 0 ms.
	const loaded = [results.getDownLoadedLatency(), results.getUpLoadedLatency()].filter(
		(ms): ms is number => ms !== undefined && ms > 0,
	)
	const points = [...results.getDownloadBandwidthPoints(), ...results.getUploadBandwidthPoints()]
	return {
		downloadBps: results.getDownloadBandwidth(),
		uploadBps: results.getUploadBandwidth(),
		loadedLatencyMs: loaded.length ? Math.max(...loaded) : undefined,
		usedBytes: points.reduce((sum, point) => sum + point.bytes, 0),
	}
}

/** Runs the Cloudflare speed test; the library loads only when a test starts. */
export const useSpeedTest = () => {
	const engineRef = useRef<SpeedTest | undefined>(undefined)
	// Bumped on every start, stop and unmount, so callbacks from a superseded run are dropped.
	const runRef = useRef(0)
	const [state, setState] = useState<SpeedTestState>({ phase: 'idle' })

	useEffect(
		() => () => {
			runRef.current++
			engineRef.current?.pause()
		},
		[],
	)

	const halt = (next: SpeedTestState | ((s: SpeedTestState) => SpeedTestState)) => {
		runRef.current++
		engineRef.current?.pause()
		setState(next)
	}

	const start = async () => {
		halt({ phase: 'running', direction: 'download' })
		const run = runRef.current
		const current = () => run === runRef.current

		let engine: SpeedTest
		try {
			const { default: SpeedTestEngine } = await import('@cloudflare/speedtest')
			engine = new SpeedTestEngine({ autoStart: false, logAimApiUrl: null, measurements: MEASUREMENTS })
		} catch {
			if (current()) {
				halt(s => ({ ...s, phase: 'error' }))
			}
			return
		}
		if (!current()) {
			return
		}
		engineRef.current = engine
		engine.onPhaseChange = ({ measurement }) => {
			if (current() && (measurement.type === 'download' || measurement.type === 'upload')) {
				setState(s => ({ ...s, direction: measurement.type as 'download' | 'upload' }))
			}
		}
		engine.onResultsChange = () => {
			if (current()) {
				setState(s => ({ ...s, ...readResults(engine.results) }))
			}
		}
		engine.onFinish = results => current() && halt({ phase: 'done', finishedAt: Date.now(), ...readResults(results) })
		engine.onError = () => current() && halt(s => ({ ...s, phase: 'error', direction: undefined }))
		engine.play()
	}

	const stop = () => halt({ phase: 'idle' })

	return { ...state, start, stop }
}

export type SpeedTestControls = ReturnType<typeof useSpeedTest>
